import { Link } from "react-router";
import { Alerta, Badge, BarraProgreso, Card, DataList, MetricCard, Row, Spinner, Stack } from "@/components";
import type { Cotizacion } from "@/lib/api";
import { ETIQUETA_ESTADO as ESTADO_COTIZACION, textoSubcategoria, TONO_ESTADO as TONO_COTIZACION } from "@/lib/cotizaciones";
import { formatFecha, formatMoneda } from "@/lib/format";
import { ETIQUETA_ESTADO, porcentaje, textoConsumido, textoDiferenciaItem, textoPartes, tonoEstado, type FilaComparacionItems } from "@/lib/presupuesto";
import { usePresupuestoRubroTab } from "./usePresupuestoRubroTab";

const claseLink = "text-primario underline-offset-2 hover:underline";
const usd = (v: string | null) => (v === null ? "—" : formatMoneda(v, "USD"));

export function PresupuestoRubroTab() {
  const t = usePresupuestoRubroTab();
  const f = t.ficha;

  const columnasCotizaciones = [
    {
      clave: "proveedor",
      titulo: "Proveedor",
      render: (c: Cotizacion) => (
        <Link to={`/cotizaciones/${c.id}`} className={claseLink}>
          {c.proveedor}
        </Link>
      ),
    },
    { clave: "subcategoria", titulo: "Subcategoría", render: (c: Cotizacion) => textoSubcategoria(c) },
    { clave: "fecha", titulo: "Fecha", render: (c: Cotizacion) => formatFecha(c.fecha) },
    { clave: "monto", titulo: "Monto", render: (c: Cotizacion) => usd(c.monto_usd) },
    { clave: "estado", titulo: "Estado", render: (c: Cotizacion) => <Badge tono={TONO_COTIZACION[c.estado]}>{ESTADO_COTIZACION[c.estado]}</Badge> },
  ];

  const columnasComparacion = [
    { clave: "item", titulo: "Ítem", render: (f: FilaComparacionItems) => <span className={f.resumen ? "font-semibold" : "whitespace-pre-line"}>{f.descripcion}</span> },
    { clave: "presupuestado", titulo: "Presupuestado", render: (f: FilaComparacionItems) => (f.resumen && f.clave !== "total" ? "" : usd(f.presupuestado)) },
    ...t.columnasComparacion.map((col, i) => ({
      clave: `c${col.cotizacion_id}`,
      titulo: col.estado === "aceptado" ? `${col.proveedor} (aceptado)` : col.proveedor,
      render: (f: FilaComparacionItems) => {
        const celda = f.celdas[i];
        if (!celda || celda.usd === null) return "—";
        return (
          <Stack espacio={1}>
            <span className={f.resumen ? "font-semibold" : undefined}>{usd(celda.usd)}</span>
            {celda.diferencia !== null && <span className="text-chico text-texto-suave">{textoDiferenciaItem(celda.diferencia)}</span>}
          </Stack>
        );
      },
    })),
  ];

  return (
    <Stack espacio={6}>
      <Link to="/presupuesto" className={claseLink}>
        ‹ Presupuesto
      </Link>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.error && <Alerta texto={t.error} />}

      {f && (
        <>
          <Row envolver espacio={2}>
            <h1 className="text-pantalla font-light tracking-[-0.03em]">{f.resumen.rubro.nombre}</h1>
            {!f.resumen.rubro.activo && <Badge>Archivado</Badge>}
            <Badge tono={tonoEstado(f.resumen.estado)}>{ETIQUETA_ESTADO[f.resumen.estado]}</Badge>
          </Row>

          <div className="grid gap-3 sm:grid-cols-3">
            <MetricCard titulo="Presupuesto" valor={usd(f.resumen.presupuesto)} detalle={f.resumen.presupuesto === null ? "Cargá cantidad y precio en los ítems" : (textoPartes(f.materiales, f.mano_obra) ?? "Suma de los ítems")} />
            <MetricCard titulo="Aceptado" valor={usd(f.resumen.aceptado_usd)} detalle="Presupuestos de proveedores aceptados" />
            <MetricCard
              titulo="Facturado"
              valor={usd(f.resumen.facturado)}
              detalle={`Pagado ${usd(f.resumen.pagado)} · consumido ${textoConsumido(f.resumen)}`}
              debajoValor={<BarraProgreso porcentaje={porcentaje(f.resumen)} tono={tonoEstado(f.resumen.estado)} etiqueta={`Consumido de ${f.resumen.rubro.nombre}`} />}
            />
          </div>

          {t.filasComparacion.length > 0 ? (
            <Card>
              <Stack espacio={3}>
                <h2 className="text-titulo font-light tracking-tight">Comparar ítem por ítem</h2>
                <p className="text-chico text-texto-suave">
                  Lo que cotizó cada proveedor para cada ítem, con las líneas de su presupuesto vinculadas a este rubro, en USD con el tipo de cambio de ese presupuesto. Las líneas se vinculan desde la ficha de cada presupuesto.
                </p>
                <DataList etiqueta={`Comparación ítem por ítem de ${f.resumen.rubro.nombre}`} columnas={columnasComparacion} filas={t.filasComparacion} claveFila={(fila) => fila.clave} />
              </Stack>
            </Card>
          ) : (
            <p className="text-texto-suave">
              Para comparar ítem por ítem, vinculá las líneas de los presupuestos recibidos a los ítems del rubro desde la ficha de cada presupuesto.
            </p>
          )}

          <Stack espacio={3}>
            <h2 className="text-titulo font-light tracking-tight">Presupuestos recibidos</h2>
            {f.resumen.etiquetas.length > 0 && <p className="text-chico text-texto-suave">{`Con las etiquetas: ${f.resumen.etiquetas.join(", ")}.`}</p>}
            <DataList
              etiqueta={`Presupuestos recibidos de ${f.resumen.rubro.nombre}`}
              columnas={columnasCotizaciones}
              filas={f.cotizaciones}
              claveFila={(c) => c.id}
              alElegirFila={t.abrirCotizacion}
              vacio={f.resumen.etiquetas.length ? "No hay presupuestos con estas etiquetas." : "Agregale una etiqueta al rubro (en Presupuesto, desplegándolo) para ver los presupuestos de los proveedores."}
            />
            <Link to="/cotizaciones" className={claseLink}>
              Ver todos los presupuestos
            </Link>
          </Stack>
        </>
      )}

    </Stack>
  );
}
